/**
 * Visitor Feature — React Query hooks
 *
 * All server state for visitor management is managed here.
 * Query keys are structured for precise cache invalidation.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import type { VisitorListParams } from '@/types'
import {
  fetchVisitors,
  fetchVisitor,
  createVisitor,
  updateVisitor,
  deleteVisitor,
  flagVisitor,
  unflagVisitor,
  fetchVisitHistory,
  logVisit,
  fetchVisitorSmsHistory,
  checkPhoneNumber,
  exportVisitors,
  sendVisitorSms,
  fetchPurposes,
  createPurpose,
  updatePurpose,
  activatePurpose,
  deactivatePurpose,
  fetchWorkflowDefaults,
  fetchWorkflowConfig,
  saveWorkflowConfig,
  resetWorkflowConfig,
  uploadVisitorPhoto,
  removeVisitorPhoto,
  fetchFieldSchema,
  fetchFieldConfig,
  updateFieldConfig,
} from './api'
import type {
  CreateVisitorPayload,
  UpdateVisitorPayload,
  LogVisitPayload,
  ExportVisitorsParams,
  CreatePurposePayload,
  UpdatePurposePayload,
  FieldDefinition,
  OrgVisitorFieldConfig,
  FieldConfigUpdate,
} from '@/types'

// ── Query key factory ────────────────────────────────────────────────────────

export const visitorKeys = {
  all:        ['visitors'] as const,
  lists:      () => [...visitorKeys.all, 'list'] as const,
  list:       (params: VisitorListParams) => [...visitorKeys.lists(), params] as const,
  detail:     (id: string) => [...visitorKeys.all, 'detail', id] as const,
  visits:     (id: string) => [...visitorKeys.all, 'visits', id] as const,
  sms:        (id: string) => [...visitorKeys.all, 'sms', id] as const,
  purposes:   ['visitor-purposes'] as const,
  fieldSchema: ['visitor-field-schema'] as const,
  fieldConfig: ['org-field-config'] as const,
}

// ── Visitor list ─────────────────────────────────────────────────────────────

export function useVisitors(params: VisitorListParams) {
  return useQuery({
    queryKey: visitorKeys.list(params),
    queryFn: () => fetchVisitors(params),
  })
}

// ── Visitor detail ───────────────────────────────────────────────────────────

export function useVisitor(id: string) {
  return useQuery({
    queryKey: visitorKeys.detail(id),
    queryFn: () => fetchVisitor(id),
    enabled: !!id,
  })
}

// ── Create visitor ───────────────────────────────────────────────────────────

export function useCreateVisitor() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: CreateVisitorPayload) => createVisitor(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: visitorKeys.lists() })
      toast.success('Visitor registered successfully.')
    },
  })
}

// ── Update visitor ───────────────────────────────────────────────────────────

export function useUpdateVisitor(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: UpdateVisitorPayload) => updateVisitor(id, payload),
    onSuccess: (updated) => {
      queryClient.setQueryData(visitorKeys.detail(id), updated)
      void queryClient.invalidateQueries({ queryKey: visitorKeys.lists() })
      toast.success('Visitor updated.')
    },
  })
}

// ── Delete visitor ───────────────────────────────────────────────────────────

export function useDeleteVisitor() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => deleteVisitor(id),
    onSuccess: (_, id) => {
      queryClient.removeQueries({ queryKey: visitorKeys.detail(id) })
      void queryClient.invalidateQueries({ queryKey: visitorKeys.lists() })
      toast.success('Visitor deleted.')
    },
  })
}

// ── Flag / Unflag ────────────────────────────────────────────────────────────

export function useFlagVisitor(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => flagVisitor(id),
    onSuccess: (updated) => {
      queryClient.setQueryData(visitorKeys.detail(id), updated)
      void queryClient.invalidateQueries({ queryKey: visitorKeys.lists() })
      toast.success('Visitor flagged.')
    },
  })
}

export function useUnflagVisitor(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => unflagVisitor(id),
    onSuccess: (updated) => {
      queryClient.setQueryData(visitorKeys.detail(id), updated)
      void queryClient.invalidateQueries({ queryKey: visitorKeys.lists() })
      toast.success('Flag removed.')
    },
  })
}

// ── Visit history ────────────────────────────────────────────────────────────

export function useVisitHistory(id: string) {
  return useQuery({
    queryKey: visitorKeys.visits(id),
    queryFn: () => fetchVisitHistory(id),
    enabled: !!id,
  })
}

export function useLogVisit(visitorId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: LogVisitPayload) => logVisit(visitorId, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: visitorKeys.visits(visitorId) })
      void queryClient.invalidateQueries({ queryKey: visitorKeys.detail(visitorId) })
      toast.success('Visit logged.')
    },
  })
}

// ── SMS history ──────────────────────────────────────────────────────────────

export function useVisitorSmsHistory(id: string) {
  return useQuery({
    queryKey: visitorKeys.sms(id),
    queryFn: () => fetchVisitorSmsHistory(id),
    enabled: !!id,
  })
}

// ── Phone duplicate check ────────────────────────────────────────────────────

export function useCheckPhone() {
  return useMutation({
    mutationFn: (phone: string) => checkPhoneNumber(phone),
  })
}

// ── Export ───────────────────────────────────────────────────────────────────

export function useExportVisitors() {
  return useMutation({
    mutationFn: (params: ExportVisitorsParams) => exportVisitors(params),
    onMutate: () => {
      toast.loading('Preparing export…', { id: 'visitor-export' })
    },
    onSuccess: () => {
      toast.success('Export ready — downloading.', { id: 'visitor-export' })
    },
    onError: () => {
      toast.error('Export failed. Please try again.', { id: 'visitor-export' })
    },
  })
}

// ── SMS quick-send ────────────────────────────────────────────────────────────

export function useSendVisitorSms(visitorId: string) {
  return useMutation({
    mutationFn: (message: string) => sendVisitorSms(visitorId, message),
    onSuccess: () => {
      toast.success('SMS sent successfully.')
    },
    onError: () => {
      toast.error('Failed to send SMS. Check credits and try again.')
    },
  })
}

// ── Visitor Purposes ─────────────────────────────────────────────────────────

export function useVisitorPurposes() {
  return useQuery({
    queryKey: visitorKeys.purposes,
    queryFn: fetchPurposes,
    staleTime: 5 * 60 * 1000, // purposes change infrequently
  })
}

export function useCreatePurpose() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: CreatePurposePayload) => createPurpose(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: visitorKeys.purposes })
      toast.success('Purpose added.')
    },
  })
}

export function useUpdatePurpose() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdatePurposePayload }) =>
      updatePurpose(id, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: visitorKeys.purposes })
      toast.success('Purpose updated.')
    },
  })
}

export function useActivatePurpose() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => activatePurpose(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: visitorKeys.purposes })
    },
  })
}

export function useDeactivatePurpose() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => deactivatePurpose(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: visitorKeys.purposes })
    },
  })
}

// ── Workflow config (V1.1) ─────────────────────────────────────────────────

export const workflowKeys = {
  defaults: ['workflow-defaults'] as const,
  config:   (purposeId: string) => ['workflow-config', purposeId] as const,
}

export function useWorkflowDefaults() {
  return useQuery({
    queryKey: workflowKeys.defaults,
    queryFn:  fetchWorkflowDefaults,
    staleTime: 10 * 60 * 1000, // system defaults never change without a deploy
  })
}

export function useWorkflowConfig(purposeId: string) {
  return useQuery({
    queryKey: workflowKeys.config(purposeId),
    queryFn:  () => fetchWorkflowConfig(purposeId),
    enabled:  !!purposeId,
    staleTime: 5 * 60 * 1000,
  })
}

export function useSaveWorkflowConfig(purposeId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (fields: FieldDefinition[]) => saveWorkflowConfig(purposeId, fields),
    onSuccess: (updated) => {
      queryClient.setQueryData(workflowKeys.config(purposeId), updated)
      // Also refresh the purposes list so workflow_type changes propagate
      void queryClient.invalidateQueries({ queryKey: visitorKeys.purposes })
      toast.success('Workflow configuration saved.')
    },
    onError: () => toast.error('Failed to save workflow configuration.'),
  })
}

export function useResetWorkflowConfig(purposeId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => resetWorkflowConfig(purposeId),
    onSuccess: (updated) => {
      queryClient.setQueryData(workflowKeys.config(purposeId), updated)
      toast.success('Workflow configuration reset to defaults.')
    },
    onError: () => toast.error('Failed to reset workflow configuration.'),
  })
}

// ── Photo capture (V1.2) ─────────────────────────────────────────────────────

export function useUploadVisitorPhoto(visitorId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (photo: Blob) => uploadVisitorPhoto(visitorId, photo),
    onSuccess: (updated) => {
      queryClient.setQueryData(visitorKeys.detail(visitorId), updated)
      toast.success('Photo saved.')
    },
    onError: () => toast.error('Failed to upload photo. Please try again.'),
  })
}

export function useRemoveVisitorPhoto(visitorId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => removeVisitorPhoto(visitorId),
    onSuccess: (updated) => {
      queryClient.setQueryData(visitorKeys.detail(visitorId), updated)
      toast.success('Photo removed.')
    },
    onError: () => toast.error('Failed to remove photo.'),
  })
}

// ── Dynamic Field Registry (V1.5) ─────────────────────────────────────────────

/**
 * Fetch the enabled field schema for registration forms.
 * Used by RegisterVisitorDrawer, KioskPage, and CaptureAgentPage.
 * Cached for 5 minutes — field schema changes are infrequent.
 */
export function useFieldSchema() {
  return useQuery<FieldDefinition[]>({
    queryKey: visitorKeys.fieldSchema,
    queryFn:  fetchFieldSchema,
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Fetch ALL field configs (enabled + disabled) for the settings page.
 * Includes the full OrgVisitorFieldConfig objects for editing.
 */
export function useFieldConfig() {
  return useQuery<OrgVisitorFieldConfig[]>({
    queryKey: visitorKeys.fieldConfig,
    queryFn:  fetchFieldConfig,
    staleTime: 60 * 1000,
  })
}

/**
 * Bulk-update field configs.
 * On success: invalidates both fieldConfig (settings) and fieldSchema (forms).
 */
export function useUpdateFieldConfig() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (updates: FieldConfigUpdate[]) => updateFieldConfig(updates),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: visitorKeys.fieldConfig })
      void queryClient.invalidateQueries({ queryKey: visitorKeys.fieldSchema })
      toast.success('Form fields saved successfully.')
    },
    onError: () => toast.error('Failed to save field configuration.'),
  })
}
